import { useContext, type ComponentType } from 'react';
import { HoruzProtocolChulakServiceSessionGuardTags } from '@ver.id/libs.internal.horuz';
import { ChulakState } from './protocol';
import { useChulakSession } from '../hooks/useChulakSession';
import { type AppInfo } from '../types';
import { JourneyContext } from '../hooks/useJourney';
import {
  type SessionData,
  type RequestToken,
  type AbortVariant,
} from '../types';

// -- Page prop types per state --
//
// The client, the request type and the requested data are not props: every
// page reads them from LayoutContext (useLayoutData).

export type AgentStartPageProps = {
  tags: HoruzProtocolChulakServiceSessionGuardTags;
  app?: AppInfo;
  onCancel: () => void;
  onBack?: () => void;
};

export type WaitingForPresentationPageProps = {
  app?: AppInfo;
  onCancel: () => void;
  onBack?: () => void;
  /** Start a fresh session for the same app (embedded Ronan's "Scan again"). */
  onRestart: () => void;
};

export type AppPairingPageProps = {
  app?: AppInfo;
  pairingCode: string;
  onCancel: () => void;
  onCompletePairing: () => void;
  onBack?: () => void;
};

export type DonePageProps = {
  app?: AppInfo;
  onComplete: () => Promise<void>;
  continueInApp: boolean;
};

export type AbortPageProps = {
  app?: AppInfo;
  errorCode?: number;
  variant?: AbortVariant;
  /** Leave and return to the client. Absent when there is no client to return to. */
  onReject?: () => void;
  onRestart: () => void;
  onBack?: () => void;
};

// -- Pages map --

export type ChulakPages = {
  Init: ComponentType;
  Auth: ComponentType;
  WaitingForPresentation: ComponentType<WaitingForPresentationPageProps>;
  AgentStart: ComponentType<AgentStartPageProps>;
  AppPairing: ComponentType<AppPairingPageProps>;
  Done: ComponentType<DonePageProps>;
  Abort: ComponentType<AbortPageProps>;
};

// -- Component --

type Props = {
  pages: ChulakPages;
  session: SessionData;
  selectedToken: RequestToken;
  onBack?: () => void;
};

/**
 * ChulakSession — Platform-agnostic Chulak flow orchestrator.
 *
 * Manages the WebSocket connection via useChulakSession and renders
 * the appropriate page component per state. Platform differences
 * (layout, footer placement) are owned by the page components
 * passed via the `pages` map.
 */
export default function ChulakSession({
  pages,
  session,
  selectedToken,
  onBack,
}: Props) {
  const journey = useContext(JourneyContext)!;

  const { ctx, actions, app, handleCancel, handleComplete, handleRestart } =
    useChulakSession(session, selectedToken);

  switch (ctx.state) {
    case ChulakState.Init:
      return <pages.Init />;

    case ChulakState.Auth:
      return <pages.Auth />;

    case ChulakState.AgentStart:
      return (
        <pages.AgentStart
          tags={ctx.tags}
          app={app}
          onCancel={handleCancel}
          onBack={onBack}
        />
      );

    case ChulakState.AppPairing:
      return (
        <pages.AppPairing
          app={app}
          pairingCode={ctx.pairingCode}
          onCancel={handleCancel}
          onCompletePairing={() => actions?.completePairing()}
          onBack={onBack}
        />
      );

    case ChulakState.WaitingForPresentation:
      return (
        <pages.WaitingForPresentation
          app={app}
          onCancel={handleCancel}
          onBack={onBack}
          onRestart={handleRestart}
        />
      );

    case ChulakState.Done:
      return (
        <pages.Done
          app={app}
          onComplete={handleComplete}
          continueInApp={ctx.continueInApp}
        />
      );

    case ChulakState.Abort:
      return (
        <pages.Abort
          app={app}
          errorCode={ctx.errorCode}
          onReject={() => journey.reject()}
          onRestart={handleRestart}
          // Any failure can be the app's: embedded offers "Use another app"
          // on every error; hosted uses it only when the app is unavailable.
          onBack={onBack}
        />
      );
  }
}
